import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  catchError,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { DealApiService } from '../services/deal-api.service';
import {
  DealResponse,
  DealSearchParams,
  DealCycle,
  DealBusinessType,
  DealPriority,
  DealBulkAction,
  DealBulkActionParams,
  DealBulkActionResponse,
  DealBulkActionFailure,
} from '../models/deal.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { LeadApiService } from '../../leads/services/lead-api.service';
import { Lead } from '../../leads/models/lead.model';
import { DealDetailModalComponent } from '../deal-detail-modal/deal-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { DealContactApiService } from '../services/deal-contact-api.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { QuoteFormModalService } from '../../quotes/services/quote-form-modal.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import {
  EntitySelectComponent,
  EntitySelectPage,
} from '../../../shared/components/entity-select/entity-select.component';
import { CrmUserResponse } from '../../users/models/user.model';
import { map } from 'rxjs/operators';
import { DealCloseReOpenmModalComponent } from '../shared/deal-close-reopen-modal/deal-close-reopen-modal.component';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { CompanyResponse } from '../../empresas/models/empresa.model';
import { countryFromPhone, COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { CountrySelectComponent } from '../../../shared/components/form/country-select/country-select.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

@Component({
  selector: 'app-deal-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    DealDetailModalComponent,
    HasPermissionDirective,
    EntitySelectComponent,
    DealCloseReOpenmModalComponent,
    CountrySelectComponent,
  ],
  templateUrl: './deal-list.component.html',
})
export class DealListComponent implements OnInit, OnDestroy {
  deals: DealResponse[] = [];

  /** Código ISO (minúscula) del país derivado del teléfono del contacto principal, para la bandera. */
  countryFlagCode(phone?: string | null): string | null {
    return countryFromPhone(phone)?.code.toLowerCase() ?? null;
  }
  /** Nombre del país derivado del teléfono (tooltip). */
  countryName(phone?: string | null): string {
    return countryFromPhone(phone)?.name ?? '';
  }

  /** Filtro por país (por prefijo telefónico del contacto principal). */
  readonly filterCountries = COUNTRIES_DATA;
  filtroPais = '';
  onCountryFilterChange(code: string): void {
    this.filtroPais = code;
    this.buscar();
  }
  loading = false;
  error = '';

  // Modal state
  showDetailModal = false;
  selectedDeal: DealResponse | null = null;

  // Dialog CloseDeal/ReOpenDeal
  showDealModal = false;
  actionChangeCycle: 'Cerrar' | 'Reabrir' | '' = '';

  // Catálogos para filtros
  dealCycles: DealCycle[] = [];
  dealBusinessTypes: DealBusinessType[] = [];
  dealPriorities: DealPriority[] = [];

  // Lead filter typeahead
  filtroLeadTerm = '';
  filtroLeadId: number | undefined;
  filtroLeadResults: Lead[] = [];
  showLeadFilterDropdown = false;
  searchingLeadFilter = false;
  private readonly leadFilterSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  /**
   * Freno de los filtros de texto (nombre del negocio y del contacto): cada tecla llamaba
   * al servidor, y de «Juan Flores» solo importaba la última de las once consultas.
   */
  private readonly busquedaTexto$ = new Subject<void>();

  // Filtros
  showFilters = false;
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_negocios';
  filtroOwner =
    Number(localStorage.getItem(this.OWNER_FILTER_KEY)) || undefined;
  filtroNombre = '';
  archived = false;
  filtroContacto = '';
  filtroDealCycle: number | undefined;
  filtroDealBusinessType: number | undefined;
  filtroDealPriority: number | undefined;
  filtroMinAmount: number | undefined;
  filtroMaxAmount: number | undefined;
  /**
   * «Todos» por defecto: con «Solo activos» el negocio recién cerrado salía del listado al
   * recargar y el contador bajaba, así que cerrar parecía eliminar. Las filas cerradas ya se
   * atenúan y ofrecen «Reabrir».
   */
  filtroActiveOnly: boolean | undefined = undefined;
  filtroClosedOnly: boolean | undefined;
  filtroclosedDateFrom = '';
  filtroclosedDateTo = '';
  // Inc 23 - filtros comerciales
  filtroCompanyId: number | undefined;
  filtroCompanyName = '';
  filtroEstimatedCloseFrom = '';
  filtroEstimatedCloseTo = '';

  currentUser: number | undefined = undefined;

  dealUserResponsables: CrmUserResponse[] = [];
  foundUserResponsables = new Set<number>();
  listUsersResponsableWithDeals: CrmUserResponse[] = [];

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'deal-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  // ── Acciones masivas ──
  selectedDealIds: number[] = [];
  isBulkActionModalOpen = false;
  bulkAction: DealBulkAction = 'CHANGE_OWNER';
  isExecutingBulk = false;
  bulkResult: DealBulkActionResponse | null = null;
  // Responsable elegido para CHANGE_OWNER (vía EntitySelectComponent)
  bulkOwnerId: number | null = null;
  // Etapa elegida para CHANGE_STAGE (id del catálogo dealCycles)
  bulkCycleId: number | null = null;

  constructor(
    private dealApi: DealApiService,
    private authService: AuthService,
    private leadApi: LeadApiService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private dealContactApi: DealContactApiService,
    private datetimeService: DatetimeService,
    private quoteFormModal: QuoteFormModalService,
    private empresaApi: EmpresaApiService,
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarDeals();

    this.busquedaTexto$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.buscar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.busquedaTexto$.complete();
  }

  private cargarCatalogos(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealApi
      .getDealCycles()
      .subscribe({ next: (data) => (this.dealCycles = data), error: () => {} });
    this.dealApi.getDealBusinessTypes(businessId).subscribe({
      next: (data) => (this.dealBusinessTypes = data),
      error: () => {},
    });
    this.dealApi.getDealPriorities(businessId).subscribe({
      next: (data) => (this.dealPriorities = data),
      error: () => {},
    });
    this.dealApi.getUsersByBusiness(businessId).subscribe({
      next: (data) => {
        this.dealUserResponsables = data || [];
      },
      error: () => {},
    });
  }

  cargarDeals(): void {
    this.loading = true;
    this.error = '';
    // Cualquier recarga (paginación, filtro, búsqueda) invalida la selección previa.
    this.selectedDealIds = [];

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.currentUser = user?.id;

    const params: DealSearchParams = {
      businessId,
      userPropietyId: this.filtroOwner ?? undefined,
      idCompany: this.filtroCompanyId ?? undefined,
      searchName: this.filtroNombre || undefined,
      contactSearchTerm: this.filtroContacto || undefined,
      phonePrefix: this.filtroPais
        ? COUNTRIES_DATA.find(c => c.code === this.filtroPais)?.dialCode || undefined
        : undefined,
      idDealCycle: this.filtroDealCycle,
      idDealBusinessType: this.filtroDealBusinessType,
      idDealPriority: this.filtroDealPriority,
      idLead: this.filtroLeadId,
      minAmount: this.filtroMinAmount ?? undefined,
      maxAmount: this.filtroMaxAmount ?? undefined,
      activeOnly: this.filtroActiveOnly ?? undefined,
      closedOnly: this.filtroActiveOnly === false ? true : undefined,
      page: this.globalPage,
      size: this.pageSize,
      sortBy: 'datetimeCreated',
      sortDirection: 'DESC',
      closedDateFrom: this.datetimeService.dateToUtcIso(
        this.filtroclosedDateFrom,
      ),
      closedDateTo: this.datetimeService.dateToUtcIso(this.filtroclosedDateTo),
      estimatedCloseDateFrom: this.toDayStartIso(this.filtroEstimatedCloseFrom),
      estimatedCloseDateTo: this.toDayEndIso(this.filtroEstimatedCloseTo),
      archived: this.archived ?? false,
    };

    this.dealApi.searchDeals(params).subscribe({
      next: (response) => {
        this.usersHasDeals(response.content);
        this.deals = response.content;
        this.globalPage = response.number ?? this.globalPage;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar oportunidades';
        this.loading = false;
      },
    });
  }
  private usersHasDeals(deals: DealResponse[]) {
    if (!deals) return;

    deals.forEach((deal) => {
      if (!deal.userPropietyId) return;
      const userResponsable = this.dealUserResponsables.find(
        (user) => deal.userPropietyId === user.id,
      );
      if (!userResponsable) return;

      if (this.foundUserResponsables.has(userResponsable.id)) return;

      this.foundUserResponsables.add(userResponsable.id);
      this.listUsersResponsableWithDeals.push(userResponsable);
    });
  }

  /** Los filtros de texto pasan por el freno; los selectores siguen llamando directo. */
  buscarTexto(): void {
    this.busquedaTexto$.next();
  }

  buscar(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarDeals();
  }

  setArchivesDeals() {
    this.archived = !this.archived;
    this.cargarDeals();
  }

  setOwnerFilter(): void {
    if (this.filtroOwner === this.currentUser) {
      this.filtroOwner = undefined;
      localStorage.removeItem(this.OWNER_FILTER_KEY);
    } else {
      this.filtroOwner = this.currentUser;
      localStorage.setItem(this.OWNER_FILTER_KEY, String(this.currentUser));
    }
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarDeals();
  }

  limpiarFiltro(): void {
    this.filtroOwner = undefined;
    this.filtroNombre = '';
    this.filtroContacto = '';
    this.filtroDealCycle = undefined;
    this.filtroDealBusinessType = undefined;
    this.filtroDealPriority = undefined;
    this.filtroMinAmount = undefined;
    this.filtroMaxAmount = undefined;
    this.filtroActiveOnly = undefined;
    this.filtroClosedOnly = undefined;
    this.filtroLeadTerm = '';
    this.filtroLeadId = undefined;
    this.filtroCompanyId = undefined;
    this.filtroCompanyName = '';
    this.filtroPais = '';
    this.filtroEstimatedCloseFrom = '';
    this.filtroEstimatedCloseTo = '';
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.filtroclosedDateFrom = '';
    this.filtroclosedDateTo = '';
    this.archived = false;
    this.cargarDeals();
  }

  // Inc 23 - rangos de fecha (ISO-8601 con offset). "to" = fin de día.
  private toDayStartIso(dateStr: string): string | undefined {
    return dateStr ? `${dateStr}T00:00:00Z` : undefined;
  }

  private toDayEndIso(dateStr: string): string | undefined {
    return dateStr ? `${dateStr}T23:59:59Z` : undefined;
  }

  // Inc 23 - buscador de empresa (server-side) para el filtro comercial
  companyFilterSearch = (
    term: string,
    page: number,
  ): Observable<EntitySelectPage<CompanyResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.empresaApi
      .searchCompanies({ businessId, searchTerm: term, page, size: 10 })
      .pipe(
        map((r) => ({ items: r.content, totalPages: r.totalPages })),
        catchError(() => of({ items: [] as CompanyResponse[], totalPages: 0 })),
      );
  };

  companyFilterLabel = (c: CompanyResponse): string => c.commercialName;

  onCompanyFilterChange(id: number | null): void {
    this.filtroCompanyId = id ?? undefined;
    this.buscar();
  }

  onOwnerFilterChange(id: number | null): void {
    this.filtroOwner = id ?? undefined;
    if (id) {
      localStorage.setItem(this.OWNER_FILTER_KEY, String(id));
    } else {
      localStorage.removeItem(this.OWNER_FILTER_KEY);
    }
    this.buscar();
  }

  // displayText para el buscador de responsable cuando el valor se fija externamente
  get ownerFilterDisplay(): string {
    if (!this.filtroOwner) return '';
    const u = this.dealUserResponsables.find((x) => x.id === this.filtroOwner);
    return u ? `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim() : '';
  }

  onLeadFilterInput(term: string): void {
    this.filtroLeadTerm = term;
    if (!term) {
      this.filtroLeadId = undefined;
      this.buscar();
    }
    this.leadFilterSearch$.next(term);
  }

  selectLeadFilter(lead: Lead): void {
    this.filtroLeadId = lead.id;
    this.filtroLeadTerm = lead.name;
    this.showLeadFilterDropdown = false;
    this.buscar();
  }

  clearLeadFilter(): void {
    this.filtroLeadId = undefined;
    this.filtroLeadTerm = '';
    this.buscar();
  }

  closeLeadFilterDropdown(): void {
    setTimeout(() => {
      this.showLeadFilterDropdown = false;
    }, 200);
  }

  nuevoDeal(): void {
    this.router.navigate(['/deals/crear']);
  }

  vistaTablero(): void {
    this.router.navigate(['/deals']);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  editarDeal(deal: DealResponse): void {
    this.router.navigate(['/deals/editar', deal.id]);
  }

  closeDeal(deal: DealResponse): void {
    if (!deal.id) return;
    this.showDealModal = true;
    this.selectedDeal = deal;
    this.actionChangeCycle = 'Cerrar';
  }

  reOpenDeal(deal: DealResponse): void {
    if (!deal.id) return;
    this.showDealModal = true;
    this.selectedDeal = deal;
    this.actionChangeCycle = 'Reabrir';
  }

  archiveDeal(deal: DealResponse): void {
    if (!deal.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealApi.archiveDeal(deal.id, businessId).subscribe({
      next: () => {
        this.cargarDeals();
        this.toast.success({
          title: 'Negocio',
          message: 'Negocio archivado',
        });
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al archivar el negocio',
        });
      },
    });
  }

  unarchiveDeal(deal: DealResponse): void {
    if (!deal.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealApi.unarchiveDeal(deal.id, businessId).subscribe({
      next: () => {
        this.cargarDeals();
        this.toast.success({
          title: 'Negocio',
          message: 'Negocio desarchivado',
        });
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al desarchivar el negocio',
        });
      },
    });
  }

  // Close / ReOpen Deal
  applyChangeCycle(data: {
    dealId: number;
    businessId: number;
    idDealCycle: number;
    description: string | undefined;
    action: string;
  }) {
    this.showDealModal = false;
    this.selectedDeal = null;
    this.actionChangeCycle = '';
    if (data.action.toLowerCase() === 'Cerrar'.toLowerCase()) {
      this.dealApi
        .closeDeal(
          data.dealId,
          data.businessId,
          data.idDealCycle,
          data.description,
        )
        .subscribe({
          next: () => {
            this.cargarDeals();
            this.toast.success({
              title: 'Negocio',
              message: 'Se cerró el negocio correctamente',
            });
          },
          error: (err) => {
            this.toast.error({
              title: 'Error',
              message: err.error?.message || 'Error al cerrar el negocio',
            });
          },
        });
      return;
    }
    this.dealApi
      .reopenDeal(data.dealId, data.businessId, data.idDealCycle)
      .subscribe({
        next: () => {
          this.cargarDeals();
          this.toast.success({
            title: 'Negocio reabierto',
            message: 'Se reabrió el negocio correctamente',
          });
        },
        error: (err) => {
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'Error al reabrir el negocio',
          });
        },
      });
  }

  async eliminarDeal(deal: DealResponse): Promise<void> {
    if (!deal.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar negocio',
      message: `¿Está seguro de eliminar "${deal.name}"?`,
    });
    if (!ok) return;
    this.dealApi.deleteDeal(deal.id, businessId).subscribe({
      next: () => {
        this.cargarDeals();
        this.toast.deleted('Negocio');
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar el negocio',
        });
      },
    });
  }

  // ──────────────────────────────────────────────
  // Selección múltiple
  // ──────────────────────────────────────────────

  toggleDealSelection(id?: number): void {
    if (id === undefined) return;
    const idx = this.selectedDealIds.indexOf(id);
    if (idx === -1) {
      this.selectedDealIds.push(id);
    } else {
      this.selectedDealIds.splice(idx, 1);
    }
  }

  isDealSelected(id?: number): boolean {
    return id !== undefined && this.selectedDealIds.includes(id);
  }

  toggleAllDeals(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedDealIds = checked
      ? this.deals
          .map((d) => d.id)
          .filter((id): id is number => id !== undefined)
      : [];
  }

  get isAllDealsSelected(): boolean {
    return (
      this.deals.length > 0 && this.selectedDealIds.length === this.deals.length
    );
  }

  // ──────────────────────────────────────────────
  // Acciones masivas
  // ──────────────────────────────────────────────

  openBulkActionModal(action: DealBulkAction): void {
    this.bulkAction = action;
    this.bulkResult = null;
    this.bulkOwnerId = null;
    this.bulkCycleId = null;
    this.isBulkActionModalOpen = true;
  }

  closeBulkActionModal(): void {
    this.isBulkActionModalOpen = false;
    this.bulkResult = null;
    this.bulkOwnerId = null;
    this.bulkCycleId = null;
  }

  onBulkOwnerChange(userId: number | null): void {
    this.bulkOwnerId = userId;
  }

  getBulkActionLabel(action: DealBulkAction): string {
    switch (action) {
      case 'CHANGE_OWNER':
        return 'Cambiar responsable';
      case 'CHANGE_STAGE':
        return 'Cambiar etapa';
      case 'DELETE':
        return 'Eliminar';
      default:
        return action;
    }
  }

  /** Nombre que devuelve el backend en `failures[].name`; nunca el id. */
  getFailureName(f: DealBulkActionFailure): string {
    return f.name?.trim() || 'Negocio sin nombre disponible';
  }

  ejecutarAccionMasiva(): void {
    if (this.selectedDealIds.length === 0) return;
    if (this.bulkAction === 'CHANGE_OWNER' && !this.bulkOwnerId) return;
    if (this.bulkAction === 'CHANGE_STAGE' && !this.bulkCycleId) return;

    let params: DealBulkActionParams | undefined;
    if (this.bulkAction === 'CHANGE_OWNER') {
      params = { userPropietyId: this.bulkOwnerId! };
    } else if (this.bulkAction === 'CHANGE_STAGE') {
      params = { cycleId: this.bulkCycleId! };
    }

    this.isExecutingBulk = true;
    this.dealApi
      .bulkActionDeals({
        action: this.bulkAction,
        dealIds: this.selectedDealIds,
        params,
      })
      .subscribe({
        next: (res) => {
          this.bulkResult = res;
          this.isExecutingBulk = false;
          this.selectedDealIds = [];
          this.cargarDeals();

          if (res.failed === 0) {
            this.toast.success({
              title: this.getBulkActionLabel(this.bulkAction),
              message: `${res.processed} negocio(s) procesado(s) correctamente.`,
            });
          } else {
            this.toast.info({
              title: 'Acción completada parcialmente',
              message: `${res.processed} procesado(s), ${res.failed} con error.`,
            });
          }
        },
        error: (err) => {
          this.isExecutingBulk = false;
          this.toast.error({
            title: 'Error',
            message:
              err.error?.message || 'No se pudo ejecutar la acción masiva.',
          });
        },
      });
  }

  // ── Picker de responsable (CHANGE_OWNER) — client-side: el endpoint de usuarios no filtra por texto ──
  userSearch = (
    term: string,
  ): Observable<EntitySelectPage<CrmUserResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.dealApi.getUsersByBusiness(businessId).pipe(
      map((users) => {
        const t = term.toLowerCase();
        const items = t
          ? users.filter((u) =>
              `${u.fullname} ${u.lastname ?? ''} ${u.mail}`
                .toLowerCase()
                .includes(t),
            )
          : users;
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 })),
    );
  };

  userLabel = (u: CrmUserResponse): string =>
    `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  // Métodos auxiliares para badges y formato
  getCycleName(id?: number): string {
    if (!id) return '—';
    const cycle = this.dealCycles.find((c) => c.id === id);
    return cycle?.name || '—';
  }

  formatCurrency(amount?: number, prefix?: string): string {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: prefix || 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(dateString?: string): string {
    return this.datetimeService.formatDate(dateString);
  }

  async nuevaCopia(deal: DealResponse): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Crear copia',
      message: `Desea crear una copia de ${deal.name}`,
    });

    if (!ok) return;

    const newCopyDeal: DealResponse = { ...deal, name: `${deal.name} Copia` };

    this.dealApi.createDeal(newCopyDeal).subscribe({
      next: async (newDeal) => {
        const associationDealContact =
          deal.contacts?.map((contact) =>
            firstValueFrom(
              this.dealContactApi.createAssociation({
                idDeal: newDeal.id,
                idContact: contact.idContact,
                businessId: newDeal.businessId,
                idDealContactRole: contact.idDealContactRole,
                isPrimary: contact.isPrimary,
                notes: contact.notes || undefined,
              }),
            ),
          ) ?? [];

        await Promise.all(associationDealContact);

        this.toast.created('Nuevo negocio');
        this.router.navigate(['/deals/editar', newDeal.id]);
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al crear la copia del negocio',
        });
      },
    });
  }

  getDealAmountDisplay(deal: DealResponse): string {
    const parts: string[] = [];
    if (deal.amount)
      parts.push(`Valor: ${this.formatCurrency(deal.amount, deal.prefix)}`);
    return parts.length > 0 ? parts.join(' | ') : '-';
  }

  isClosed(deal: DealResponse): boolean {
    return !!deal.datetimeClosed;
  }

  getDealStatusBadgeColor(deal: DealResponse): BadgeColor {
    if (this.isClosed(deal)) {
      // Si está cerrado, verificar si es ganado o perdido basado en el cycle name
      const cycleName = deal.dealCycleName?.toLowerCase();
      if (cycleName?.includes('ganado') || cycleName?.includes('won')) {
        return 'success';
      }
      if (cycleName?.includes('perdido') || cycleName?.includes('lost')) {
        return 'error';
      }
      return 'light';
    }
    return 'info';
  }

  // Paginación
  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarDeals();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }
  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }
  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  // Acción rápida: abrir el wizard de cotización con este negocio preseleccionado.
  nuevaCotizacion(deal: DealResponse): void {
    this.quoteFormModal.open({ dealId: deal.id });
  }

  verDetalle(deal: DealResponse): void {
    this.selectedDeal = deal;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedDeal = null;
  }

  onDealUpdated(): void {
    this.cerrarModal();
    this.cargarDeals();
  }
}
